// ── What Global Conversation V2 watches ─────────────────────────────────────────────────────────
//
// V2 reacts to things V1's code does (a pack filed, a task adopted, an answer landing) and leaves
// that code exactly as it is, so the feature can be dropped with its branch. This is the one place
// that listens, installed only when the flag is on (`chatTopicsV2Plugins` answers [] otherwise,
// which is how the app stays today's app with it off).
//
// The writes it starts are the save hooks, and each answers a save the user confirmed. A suggestion
// never reaches this file.

import router from '@/router'
import { liveTurns } from '@/lib/chatRail'
import { DEEP_LINK_QUERY, GLOBAL_CONVERSATION_V2, selectionQuestion } from '@/lib/chatTopicsV2'
import { isHomePath } from '@/lib/searchMode'

/** The answer is journalled just after the response, so `place` waits this long to find it. */
export const PLACE_DELAY_MS = 1500

const NS = 'chatTopicsV2'

/** How many questions of the live conversation have been answered in this tab. */
export function answeredCount(turns) {
  return liveTurns(turns).filter((t) => !t.restored && !t.asking && String(t.question || '').trim() && (String(t.answer || '').trim() || t.blocks?.length)).length
}

const findTurn = (store, turnId) => (store.state.timelineSearch?.conversation || []).find((t) => t?.id === turnId) || null

/** Install on a store. Exported for the tester; the app reaches it through `chatTopicsV2Plugins`. */
export function installChatTopicsV2(store, { nav = () => router, wait = setTimeout, stop = clearTimeout } = {}) {
  // A created task's block loses its destination when it is replaced, so it is read beforehand.
  const pending = new Map()
  // The ticked research is cleared right after a pack is filed, before `after` runs, so the question
  // it was asked under is read here, while the board still holds it.
  const asked = []

  store.subscribeAction({
    before(action) {
      if (action.type === 'research/filedIntoPack') {
        asked.push(selectionQuestion(store.getters['workboard/items']))
        return
      }
      if (action.type !== 'chatActions/adoptCreated') return
      const { turnId, key } = action.payload || {}
      const block = (findTurn(store, turnId)?.blocks || []).find((b) => b?.key === key)
      pending.set(`${turnId}:${key}`, block?.gcV2Destination || null)
    },
    async after(action) {
      if (action.type === 'research/filedIntoPack') {
        const id = typeof action.payload === 'string' ? action.payload : action.payload?.id
        store.dispatch(`${NS}/linkFiledPack`, { packId: id, question: asked.shift() || '' })
        return
      }
      if (action.type !== 'chatActions/adoptCreated') return
      const { turnId, key, task, ongoing } = action.payload || {}
      const chosen = pending.get(`${turnId}:${key}`) || null
      pending.delete(`${turnId}:${key}`)
      if (!chosen) return
      // Create joined the Ongoing already; a draft saved from the dock has not, and needs it here.
      if (!ongoing?.id && task?.timelineId) {
        try {
          await store.dispatch('ongoing/linkTimeline', { id: chosen.ongoingId, timelineId: task.timelineId }, { root: true })
        } catch {
          return
        }
      }
      store.dispatch(`${NS}/linkCreatedTask`, { task, turn: findTurn(store, turnId), destination: chosen })
    },
  })

  // An answer landed: once it is journalled, place it so the rail shows the real topic.
  let placing = null
  store.watch(
    (state) => (state.chatSessions?.viewingId ? -1 : answeredCount(state.timelineSearch?.conversation)),
    (now, before) => {
      if (now <= before || before < 0) return
      stop(placing)
      placing = wait(() => store.dispatch(`${NS}/afterAnswer`), PLACE_DELAY_MS)
    },
  )

  // The Briefing's "N Conversations" counts the topics linked to the Ongoing it is about.
  store.watch(
    (_, getters) => getters['briefing/story']?.id || null,
    (ongoingId) => { if (ongoingId) store.dispatch(`${NS}/ensureOngoing`, { ongoingId }) },
  )

  // The deep link, `/home?gcMessage=<id>`. The address is cleaned afterwards so a reload is an
  // ordinary Home, and a link opened before sign-in has finished waits for it.
  let waiting = null
  const follow = async (messageId) => {
    const opened = await store.dispatch(`${NS}/openAround`, messageId)
    if (opened) nav().replace('/home').catch(() => {})
    return opened
  }
  const arrive = (to) => {
    const messageId = to?.query?.[DEEP_LINK_QUERY]
    if (!messageId || !isHomePath(to.path)) return
    if (store.getters['user/isAuthenticated']) follow(String(messageId))
    else waiting = String(messageId)
  }
  // The router imports the store, so it does not exist yet while the store is being created. This
  // waits for the modules to finish evaluating, and reads the route it may already be on.
  Promise.resolve().then(() => {
    nav().afterEach(arrive)
    arrive(nav().currentRoute?.value)
  })
  store.watch(
    (_, getters) => !!getters['user/isAuthenticated'],
    (signedIn) => {
      if (!signedIn || !waiting) return
      const messageId = waiting
      waiting = null
      follow(messageId)
    },
  )
}

/** The store's plugin list entry: nothing at all with the flag off. */
export const chatTopicsV2Plugins = (enabled = GLOBAL_CONVERSATION_V2) => (enabled ? [installChatTopicsV2] : [])
